/**
 * Nav v2 — live-role ↔ prototype alias cross-map.
 *
 * `nav-v2.ts` declares per-item visibility using 7 prototype aliases
 * (`COORDINATOR / DON / QUALITY / FACILITY / FINANCE / EXEC / CARE_WORKER`)
 * inherited from `nav-redesign/data.jsx`. The platform's authoritative role
 * vocabulary is much wider — 16+ live `users.role` values (see
 * `prod-schema-dump.sql` users_role_check + `dashboard-layout.tsx:1900-1920`
 * `ALL_APP_NAV_ROLES`).
 *
 * This module is the single source of truth for that mapping. Used by the
 * Phase 2 sidebar to perform a coarse first-pass alias filter. The
 * authoritative gates remain `isRoleAllowedForPath`
 * (`apps/web/src/lib/route-access.ts`) and `isModuleNavigationVisible`
 * (`apps/web/src/lib/module-access.ts`) per spec §0 hard constraint #2.
 *
 * Design choices:
 *   - **Reverse mapping by live role.** Each live `users.role` gets a SET of
 *     aliases it qualifies for. This is more useful than alias→[live-roles]
 *     because the sidebar's hot path is "given this user's live role, does
 *     this item's `roles[]` alias-set intersect?".
 *   - **Privileged roles qualify for ALL aliases.** SYSTEM_ADMIN and
 *     TENANT_ADMIN see everything subject to the live RBAC + module gates.
 *     Hiding nav for these roles would degrade the platform-admin
 *     experience for no real entitlement gain.
 *   - **Multi-hat roles get multiple aliases.** REGION_MANAGER qualifies
 *     for FACILITY (they manage facilities) + COORDINATOR (they may
 *     coordinate care directly) + EXEC (board reporting). CLINICAL_LEAD
 *     qualifies for DON + COORDINATOR (clinical-team lead) + CARE_WORKER
 *     (they do hands-on clinical work).
 *
 * Decisions: `docs/nav-v2/decisions.md` Q6.
 */

import { ALL_ROLE_ALIASES, type RoleAlias } from './nav-v2';

/**
 * The complete set of live `users.role` enum values we may encounter.
 * Mirrors `users_role_check` in `supabase/live-schema-2026-05-24.checker.sql`
 * (28 roles, post the HEAD_OFFICE expansion). `prod-schema-dump.sql` is
 * stale — do NOT use it as the source of truth for this list.
 *
 * `CLIENT` and `FAMILY_CARER` are deliberately excluded — they are
 * portal-only, not dashboard users; the dashboard layout already
 * redirects them client-side.
 *
 * Phase 2 audit Finding A added 8 HEAD_OFFICE-class roles + the
 * `COMPLIANCE_OFFICER` role that the original Phase 0 mapping missed
 * because Phase 0 read from prod-schema-dump.sql (stale baseline).
 * The completeness regression test at the bottom of
 * `apps/web/src/__tests__/nav-v2-ia.test.ts` is the regression-net —
 * it iterates `INTERNAL_APP_ROLES` from `route-access.ts` and asserts
 * every entry has alias coverage here.
 */
export type LiveRole =
  | 'SYSTEM_ADMIN'
  | 'TENANT_ADMIN'
  | 'CEO'
  | 'OWNER'
  | 'OPERATIONS_DIRECTOR'
  | 'CLINICAL_DIRECTOR'
  | 'FINANCE_DIRECTOR'
  | 'QUALITY_DIRECTOR'
  | 'HR_DIRECTOR'
  | 'COORDINATOR'
  | 'REGISTERED_NURSE'
  | 'ENROLLED_NURSE'
  | 'CARE_WORKER'
  | 'ALLIED_HEALTH'
  | 'CLINICAL_LEAD'
  | 'AGENCY_CONTRACTOR'
  | 'FINANCE_OFFICER'
  | 'PAYROLL_OFFICER'
  | 'COMPLIANCE_OFFICER'
  | 'FACILITY_MANAGER'
  | 'QUALITY_OFFICER'
  | 'CASE_MANAGER'
  | 'LIFESTYLE_OFFICER'
  | 'REGION_MANAGER'
  | 'REGION_COMPLIANCE_OFFICER'
  | 'REGION_FINANCE_OFFICER'
  | 'TEAM_LEADER';

/**
 * Each live role's qualifying alias set. An empty array means the role
 * has no alias coverage and the sidebar item must rely on the live RBAC
 * gates to be visible (which is the fallback path anyway).
 */
const LIVE_ROLE_TO_ALIASES: Readonly<Record<LiveRole, readonly RoleAlias[]>> = Object.freeze({
  // ── Platform-privileged: see everything (gated by live RBAC) ────────
  SYSTEM_ADMIN: ALL_ROLE_ALIASES,
  TENANT_ADMIN: ALL_ROLE_ALIASES,

  // ── HEAD_OFFICE-class executive (organisation-wide leadership) ──────
  // Added Phase 2 (audit Finding A) — these 7 roles exist in the live
  // users_role_check constraint and were missing from the original map,
  // causing users with these roles to see an empty sidebar.
  CEO: ['EXEC'],
  OWNER: ['EXEC'],
  OPERATIONS_DIRECTOR: ['EXEC', 'FACILITY', 'COORDINATOR'],
  CLINICAL_DIRECTOR: ['EXEC', 'DON'],
  FINANCE_DIRECTOR: ['EXEC', 'FINANCE'],
  QUALITY_DIRECTOR: ['EXEC', 'QUALITY'],
  HR_DIRECTOR: ['EXEC', 'COORDINATOR'],

  // ── Region-level: multi-facility leadership ──────────────────────────
  REGION_MANAGER: ['EXEC', 'FACILITY', 'COORDINATOR'],
  REGION_COMPLIANCE_OFFICER: ['QUALITY', 'EXEC'],
  REGION_FINANCE_OFFICER: ['FINANCE', 'EXEC'],

  // ── Facility / coordination ──────────────────────────────────────────
  FACILITY_MANAGER: ['FACILITY', 'EXEC', 'COORDINATOR'],
  COORDINATOR: ['COORDINATOR', 'CARE_WORKER'],
  CASE_MANAGER: ['COORDINATOR'],
  TEAM_LEADER: ['COORDINATOR', 'CARE_WORKER'],

  // ── Clinical leadership ──────────────────────────────────────────────
  // DON = Director of Nursing is the live CLINICAL_LEAD role. Treats
  // them as DON for nav purposes AND as a senior coordinator AND as a
  // frontline care worker (they cover hands-on shifts).
  CLINICAL_LEAD: ['DON', 'COORDINATOR', 'CARE_WORKER'],

  // ── Frontline care ───────────────────────────────────────────────────
  REGISTERED_NURSE: ['CARE_WORKER'],
  ENROLLED_NURSE: ['CARE_WORKER'],
  CARE_WORKER: ['CARE_WORKER'],
  ALLIED_HEALTH: ['CARE_WORKER'],
  LIFESTYLE_OFFICER: ['CARE_WORKER'],
  AGENCY_CONTRACTOR: ['CARE_WORKER'],

  // ── Specialist functions ─────────────────────────────────────────────
  QUALITY_OFFICER: ['QUALITY'],
  FINANCE_OFFICER: ['FINANCE'],
  PAYROLL_OFFICER: ['FINANCE'],
  COMPLIANCE_OFFICER: ['QUALITY'],
  // (Phase 2 audit: removed the phantom 'HR' entry — not in users_role_check.)
});

/**
 * Does the given live role qualify for at least one of the supplied
 * prototype aliases? Used by the sidebar's first-pass filter.
 *
 * Returns true if `aliases` is empty (defensive — an item with no
 * declared `roles[]` is visible to everyone, matching `data.jsx`
 * `ALL_ROLES` default semantics).
 *
 * Returns false if the live role is unrecognised — fail closed.
 */
export function liveRoleMatchesAlias(
  liveRole: string | null | undefined,
  aliases: readonly RoleAlias[],
): boolean {
  if (!aliases || aliases.length === 0) return true;
  if (!liveRole) return false;
  const aliasSet = LIVE_ROLE_TO_ALIASES[liveRole as LiveRole];
  if (!aliasSet) return false;
  return aliases.some((a) => aliasSet.includes(a));
}

/**
 * Inverse lookup for documentation / testing — given a prototype alias,
 * which live roles qualify? O(n) — only meant for tests and tooling.
 */
export function liveRolesForAlias(alias: RoleAlias): LiveRole[] {
  const out: LiveRole[] = [];
  for (const [role, aliases] of Object.entries(LIVE_ROLE_TO_ALIASES) as [
    LiveRole,
    readonly RoleAlias[],
  ][]) {
    if (aliases.includes(alias)) out.push(role);
  }
  return out;
}

/**
 * Export for tests that want to assert the full mapping shape.
 * NOT for use by render code — render code uses `liveRoleMatchesAlias`.
 */
export const LIVE_ROLE_ALIAS_MAP = LIVE_ROLE_TO_ALIASES;

/**
 * Pick the "primary" alias for a live role — used to seed
 * `DEFAULT_PINS_BY_ROLE` when no user-set pins exist. Returns the most
 * specific role-of-work, not the broadest privilege. Examples:
 *   - SYSTEM_ADMIN / TENANT_ADMIN → EXEC (they're admins by privilege
 *     but their primary work is multi-facility leadership)
 *   - CLINICAL_LEAD → DON (DON pins are clinical-lead's daily set)
 *   - CARE_WORKER / REGISTERED_NURSE / ENROLLED_NURSE → CARE_WORKER
 *   - Unknown → CARE_WORKER (most conservative — frontline default set)
 */
const LIVE_ROLE_PRIMARY_ALIAS: Readonly<Record<LiveRole, RoleAlias>> = Object.freeze({
  SYSTEM_ADMIN: 'EXEC',
  TENANT_ADMIN: 'EXEC',
  CEO: 'EXEC',
  OWNER: 'EXEC',
  OPERATIONS_DIRECTOR: 'EXEC',
  CLINICAL_DIRECTOR: 'DON',
  FINANCE_DIRECTOR: 'FINANCE',
  QUALITY_DIRECTOR: 'QUALITY',
  HR_DIRECTOR: 'EXEC',
  REGION_MANAGER: 'EXEC',
  REGION_COMPLIANCE_OFFICER: 'QUALITY',
  REGION_FINANCE_OFFICER: 'FINANCE',
  FACILITY_MANAGER: 'FACILITY',
  COORDINATOR: 'COORDINATOR',
  CASE_MANAGER: 'COORDINATOR',
  TEAM_LEADER: 'COORDINATOR',
  CLINICAL_LEAD: 'DON',
  REGISTERED_NURSE: 'CARE_WORKER',
  ENROLLED_NURSE: 'CARE_WORKER',
  CARE_WORKER: 'CARE_WORKER',
  ALLIED_HEALTH: 'CARE_WORKER',
  LIFESTYLE_OFFICER: 'CARE_WORKER',
  AGENCY_CONTRACTOR: 'CARE_WORKER',
  QUALITY_OFFICER: 'QUALITY',
  FINANCE_OFFICER: 'FINANCE',
  PAYROLL_OFFICER: 'FINANCE',
  COMPLIANCE_OFFICER: 'QUALITY',
});

export function primaryAliasForLiveRole(liveRole: string | null | undefined): RoleAlias {
  if (!liveRole) return 'CARE_WORKER';
  return LIVE_ROLE_PRIMARY_ALIAS[liveRole as LiveRole] ?? 'CARE_WORKER';
}
